import React, { forwardRef } from 'react';
import { PropTypes } from '../../../utils/propTypes';
import styles from './Button.module.css';

/**
 * Button — the shared action-button primitive.
 *
 * Flat, token-based, theme-flipping. Renders a <button>, or an
 * <a role="button"> when `href` is provided. Covers the action-button
 * surface used across the app; segmented-toggle and upload-dropzone are
 * intentionally separate primitives (different structure).
 *
 * Props:
 *   variant   — 'primary' | 'secondary' | 'danger' | 'ghost' | 'link'  (default 'primary')
 *   size      — 'xs' | 'sm' | 'md' | 'lg'  (default 'md').
 *               Legacy 'small' | 'medium' | 'large' are accepted as aliases.
 *   iconOnly  — square icon button; pass `aria-label` (a dev warning fires if missing).
 *   startIcon / endIcon — leading / trailing icon nodes.
 *   loading   — show inline spinner + disable interaction.
 *   disabled  — disable.
 *   fullWidth — stretch to 100% width.
 *   href / external — render as <a>; `external` adds target=_blank rel=noopener.
 *   type      — 'button' | 'submit' | 'reset'  (default 'button').
 *   …plus onClick/onFocus/onBlur, className, style, aria-label, data-testid, ref.
 */

// Legacy size names → canonical scale, so existing consumers keep working.
const SIZE_ALIAS = {
  small: 'sm',
  medium: 'md',
  large: 'lg',
  xlarge: 'lg',
};

const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  iconOnly = false,
  loading = false,
  disabled = false,
  fullWidth = false,
  startIcon,
  endIcon,
  href,
  external = false,
  className,
  style,
  onClick,
  onFocus,
  onBlur,
  type = 'button',
  'aria-label': ariaLabel,
  'data-testid': testId,
  ...props
}, ref) => {
  const isLink = Boolean(href);
  const isDisabled = disabled || loading;
  const resolvedSize = SIZE_ALIAS[size] || size;

  if (process.env.NODE_ENV !== 'production' && iconOnly && !ariaLabel) {
    // eslint-disable-next-line no-console
    console.warn('Button: `iconOnly` buttons must be given an `aria-label` for accessibility.');
  }

  const buttonClasses = [
    styles.button,
    styles[variant],
    styles[resolvedSize],
    iconOnly && styles.iconOnly,
    fullWidth && styles.fullWidth,
    loading && styles.loading,
    isDisabled && styles.disabled,
    className,
  ].filter(Boolean).join(' ');

  const buttonProps = {
    className: buttonClasses,
    style,
    onClick: isDisabled ? undefined : onClick,
    onFocus,
    onBlur,
    disabled: isDisabled,
    'aria-label': ariaLabel,
    'aria-busy': loading || undefined,
    'data-testid': testId,
    ref,
    ...props,
  };

  const renderContent = () => (
    <>
      {startIcon && (
        <span className={styles.iconStart} aria-hidden="true">
          {startIcon}
        </span>
      )}
      {loading && (
        <span className={styles.loadingSpinner} aria-hidden="true">
          <svg viewBox="0 0 24 24" className={styles.spinner}>
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
        </span>
      )}
      {children != null && children !== '' && (
        <span className={styles.content}>{children}</span>
      )}
      {endIcon && (
        <span className={styles.iconEnd} aria-hidden="true">
          {endIcon}
        </span>
      )}
    </>
  );

  if (isLink) {
    const linkProps = {
      ...buttonProps,
      href: isDisabled ? undefined : href,
      target: external ? '_blank' : undefined,
      rel: external ? 'noopener noreferrer' : undefined,
      role: 'button',
    };
    // <a> has no `disabled` attribute; reflect state via aria instead.
    delete linkProps.disabled;
    linkProps['aria-disabled'] = isDisabled || undefined;

    return (
      <a {...linkProps}>
        {renderContent()}
      </a>
    );
  }

  return (
    <button type={type} {...buttonProps}>
      {renderContent()}
    </button>
  );
});

Button.displayName = 'Button';

// PropTypes for runtime validation. variant/size are plain strings now
// (semantic variant set is owned by this component, not the product-theme enum).
Button.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.string,
  size: PropTypes.string,
  iconOnly: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  href: PropTypes.string,
  external: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  type: PropTypes.string,
  'aria-label': PropTypes.string,
  'data-testid': PropTypes.string,
};

export default Button;
